import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Skeleton,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconMapping from "src/utils/iconMapping";
import { BankingBreakdownStyles } from "./styles/BankingBreakdown.styles";
import IntBanking from "src/components/banking/IntBanking";
import AusBanking from "src/components/banking/AusBanking";
import {
  useGetUserDatav2Query,
  useAcceptDepositDisclosureMutation,
} from "src/store/features/api/generated";
import { trackEvent } from "src/Amplitude";
import CopySnackbar from "src/components/common/CopySnackbar";
import Title from "../common/Title";
import usePermission from "src/utils/hooks/usePermission";
import GuestBanner from "../dashboard/GuestBanner";
import {
  useUserSlice,
  setUserDepositDisclosures,
  type DepositDisclosureData,
} from "src/store/features/user.slice";
import { useAppDispatch } from "src/store/store";
import LockIcon from "src/icons/LockIcon";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";
import useTransactionDisclosure from "src/utils/hooks/useTransactionDisclosure";
import { useDepositDisclosure } from "src/utils/hooks/useLegalDisclosure";
import { isUkCountry } from "src/utils/isUkCountry";
import { isAuCountry } from "src/utils/isAuCountry";

type CurrencyCodes = "AUD" | "USD" | "EUR" | "NZD" | "GBP";

export default function BankingBreakdown() {
  const history = useHistory();

  // state
  const [copy, setCopy] = useState(false);
  const [currencyCode, setCurrencyCode] = React.useState<CurrencyCodes>("AUD");
  const [showDisclosureModal, setShowDisclosureModal] = React.useState<boolean>(false);

  // styles
  const classes = BankingBreakdownStyles();

  // redux
  const dispatch = useAppDispatch();
  const { data, isSuccess, isLoading } = useGetUserDatav2Query();
  const { accountLock, depositDisclosures, address } = useUserSlice();
  const [acceptDepositDisclosure] = useAcceptDepositDisclosureMutation();
  const { note: depositNote, version: depositVersion } = useDepositDisclosure();

  // Fiat deposit disclosure: show state-based disclosure when viewing banking (once per visit if not accepted)
  const hasAcceptedFiatDisclosure = React.useMemo(
    () =>
      depositDisclosures.some(
        (d) =>
          d.type === "Fiat" &&
          d.accepted &&
          (depositVersion == null || d.disclosureVersion === depositVersion),
      ),
    [depositDisclosures, depositVersion],
  );

  const {
    transactionDisclosure,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure((disclosureId) => {
    const userState = address?.state?.trim().toLowerCase() ?? "";

    acceptDepositDisclosure({
      acceptDepositDisclosureDto: {
        state: userState,
        isAccepted: true,
        type: "Fiat",
        ...(depositVersion != null && {
          disclosureVersion: depositVersion,
        }),
      },
    })
      .unwrap()
      .then(() => {
        // Optimistically append new acceptance so Redux matches backend's append-only list
        const now = Date.now();
        const newEntry: DepositDisclosureData = {
          state: userState,
          accepted: true,
          timestamp: now,
          type: "Fiat",
          ...(depositVersion != null && {
            disclosureVersion: depositVersion,
          }),
        };
        dispatch(setUserDepositDisclosures([...depositDisclosures, newEntry]));
      })
      .catch((err) => {
        console.error("Failed to save Fiat deposit disclosure acceptance", err);
      });

    setShowDisclosureModal(false);
  });

  // Only redirect to dashboard when user dismisses (backdrop/escape), not when they accept the disclosure
  const disclosureModalProps = React.useMemo(
    () => ({
      ...modalProps,
      open: showDisclosureModal && !!transactionDisclosure,
      noticeTextOverride: depositNote,
      blurBackdrop: true, // Blur banking details behind the modal
      onClose: (_event?: any, reason?: string) => {
        setShowDisclosureModal(false);
        handleDisclosureClose();
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          history.push("/portfolio");
        }
      },
    }),
    [modalProps, showDisclosureModal, transactionDisclosure, depositNote, handleDisclosureClose, history],
  );

  const bankingDisclosureShownRef = React.useRef(false);
  React.useEffect(() => {
    if (
      transactionDisclosure &&
      !hasAcceptedFiatDisclosure &&
      !bankingDisclosureShownRef.current &&
      !isLoading
    ) {
      bankingDisclosureShownRef.current = true;
      setShowDisclosureModal(true);
    }
    if (!transactionDisclosure) {
      bankingDisclosureShownRef.current = false;
    }
  }, [transactionDisclosure, hasAcceptedFiatDisclosure, isLoading]);

  // variables
  // UK customers can only deposit in GBP (dropdown hidden below); all other
  // customers keep the full currency list, including GBP.
  const isUkResident = isUkCountry(data?.address?.country);
  const currencyCodes: CurrencyCodes[] = isUkResident
    ? ["GBP"]
    : ["AUD", "USD", "EUR", "NZD", "GBP"];
  const defaultCurrency: CurrencyCodes = isUkResident ? "GBP" : "AUD";
  const title = { partB: "Select your currency" };
  const { userPermission } = usePermission();
  // this effect will default to country based on user's address
  useEffect(() => {
    if (data?.defaultCurrencyConfig) {
      let currencyCode = currencyCodes.find(
        (code: string) => code === data?.defaultCurrencyConfig.code,
      );
      if (!currencyCode) currencyCode = defaultCurrency; // if not offered, use the default for the region
      setCurrencyCode(currencyCode);
    }
    if (userPermission === "guest") {
      setCurrencyCode("AUD");
    }

    return () => setCurrencyCode("AUD");
  }, [isSuccess]);

  // functions
  const copyToClipboard = (el: any) => {
    if (userPermission === "guest") return; // is guest, do not copy
    // end function if false
    if (!window.isSecureContext) {
      return;
    }
    if (el.content === "CustomerId") {
      navigator.clipboard.writeText(`${data?.details.customerId} ${data?.details.name}`);
    } else {
      navigator.clipboard.writeText(el.content);
    }
    setCopy((prev) => !prev);
    setTimeout(() => {
      setCopy((prev) => !prev);
    }, 1500);
  };

  const handleChange = (event: any) => {
    setCurrencyCode(event.target.value);
  };

  // The selected currency must always be one that is offered to this customer;
  // fall back to the regional default if state is stale or out of range.
  const selectedCurrency: CurrencyCodes = currencyCodes.includes(currencyCode)
    ? currencyCode
    : defaultCurrency;

  // AU clients and residents have the ability for instant deposits through Monoova
  const isAUClientAndResident = selectedCurrency === "AUD" && isAuCountry(data?.address?.country);

  const subsidiary = data?.subsidiary ?? "AU"; // default to AU if subsidiary not found
  // US Subsidiaries and UK customers have only one currency option, so hide the dropdown
  const hideCurrencySelection = subsidiary === "US" || isUkResident;

  if (accountLock && accountLock.locked) {
    return (
      <Grid
        container
        className={classes.container}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          textAlign: "center",
          padding: "40px 20px",
        }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            maxWidth: "400px",
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#F8F9F9",
              marginBottom: "8px",
            }}>
            <LockIcon />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "#002035",
              marginBottom: "8px",
            }}>
            Account Locked
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#5C6469",
              lineHeight: 1.5,
            }}>
            Your account has been locked. Please contact us at support@calebandbrown.com for
            assistance.
          </Typography>
          {accountLock.reason && (
            <Typography
              variant="body2"
              sx={{
                color: "#9CA3A8",
                fontStyle: "italic",
                marginTop: "8px",
              }}>
              {accountLock.reason}
            </Typography>
          )}
        </Box>
      </Grid>
    );
  }

  return (
    <>
      <Grid container mb={8}>
        {isLoading ? (
          <Stack spacing={4}>
            <Skeleton sx={{ borderRadius: "4px" }} variant="rectangular" width="100%" height={20} />
            <Skeleton
              sx={{ borderRadius: "4px" }}
              variant="rectangular"
              width="100%"
              height={150}
            />
            <Skeleton sx={{ borderRadius: "4px" }} variant="rectangular" width="100%" height={30} />
            <Skeleton sx={{ borderRadius: "4px" }} variant="rectangular" width="100%" height={20} />
            <Skeleton sx={{ borderRadius: "4px" }} variant="rectangular" width="100%" height={30} />
            <Skeleton sx={{ borderRadius: "4px" }} variant="rectangular" width="100%" height={20} />
            <Skeleton sx={{ borderRadius: "4px" }} variant="rectangular" width="100%" height={50} />
          </Stack>
        ) : (
          <>
            {userPermission === "guest" && (
              <Grid item xs={12}>
                <GuestBanner
                  variant={2}
                  descriptionPrefix={"To deposit to your account"}
                  featureName={"Banking"}
                />
              </Grid>
            )}
            <>
              <Grid item xs={12} display={hideCurrencySelection ? "none" : "block"}>
                <Box mb={2}>
                  <Title title={title} />
                </Box>
                <Box className={classes.currencyContainer}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">Currency</InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={selectedCurrency}
                      label="country"
                      onChange={handleChange}>
                      {currencyCodes.map((code: string, i: number) => {
                        return (
                          <MenuItem
                            value={code}
                            key={i}
                            onClick={() =>
                              trackEvent(`Select_Banking_Details_Currency`, {
                                token: code,
                              })
                            }>
                            <Grid item display="flex">
                              {IconMapping(code.toLocaleLowerCase())}
                              <Typography className={classes.symbol}>{code}</Typography>
                            </Grid>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              <div
                style={{
                  filter: userPermission === "guest" ? "blur(4px)" : "none",
                  pointerEvents: userPermission === "guest" ? "none" : "auto",
                  userSelect: userPermission === "guest" ? "none" : "auto",
                  width: "100%",
                }}>
                {isAUClientAndResident ? (
                  <AusBanking copyToClipboard={copyToClipboard} countryCode={"AUD"} />
                ) : (
                  <IntBanking
                    copyToClipboard={copyToClipboard}
                    country={selectedCurrency}
                    countryCode={data?.address?.country || "AU"}
                    subsidiary={subsidiary}
                    clientDetails={{
                      name: data ? data.details.name : "",
                      customerId: data ? data.details.customerId : "",
                    }}
                  />
                )}
              </div>
            </>
          </>
        )}
      </Grid>
      <CopySnackbar open={copy} handleClose={setCopy} />
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </>
  );
}
